import React, { useEffect, useState } from 'react';
import SEO from './components/SEO';
import {
  Button, Dialog, DialogTitle, DialogContent,
  DialogActions, Typography, Snackbar
} from '@mui/material';
import { AppProvider, useApp } from './context/AppContext';
import { SNACKBAR_AUTO_HIDE_MS } from './utils/constants';
import ConversionPage from './components/ConversionPage';
import BillingPortal from './components/BillingPortal';
import SubscriptionConfirmed from './components/SubscriptionConfirmed';
import AuthModal from './components/auth/AuthModal';
import ConsentBanner from './components/ConsentBanner';
import AuthButtonEmbed from './components/embed/AuthButtonEmbed';
import PlanButtonEmbed from './components/embed/PlanButtonEmbed';
import {
  listenForEmbedRequests, REQUEST_AUTH, REQUEST_CHECKOUT, REQUEST_ACCOUNT,
} from './utils/embedChannel';
import { isAuthEmbed, isButtonEmbed, isAuthPopup, planEmbedConfig } from './utils/embedOptions';

function AppShell() {
  const {
    currentPage, snackbar, setSnackbar, confirmDialog, setConfirmDialog,
    authModalOpen, setAuthModalOpen, setAuthTab, handleCheckout, handleLogout,
    setCurrentPage, email, token,
  } = useApp();

  // Opened when a signed-in standalone ?embed=auth button is clicked: that
  // frame is button-sized, so the billing/logout choices render here. With
  // ?hideAuth=1 this is the page's only path to logout.
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);

  // Answer requests from button-only frames on the same page: this frame is
  // full height, so dialogs and confirmations belong here.
  useEffect(() => {
    if (isButtonEmbed) return undefined;
    return listenForEmbedRequests({
      [REQUEST_AUTH]: (payload) => { setAuthTab(payload?.tab || 'signin'); setAuthModalOpen(true); },
      [REQUEST_CHECKOUT]: (payload) => { if (payload?.planId) handleCheckout(payload.planId); },
      [REQUEST_ACCOUNT]: () => setAccountDialogOpen(true),
    });
  }, [setAuthModalOpen, setAuthTab, handleCheckout]);

  // Dismissing the dialog in the sign-in popup means "cancel" — there is
  // nothing behind it — so close the window rather than leave a blank one. The
  // signed-in case is handled in AppContext, which closes after reporting back.
  useEffect(() => {
    if (!isAuthPopup || authModalOpen || token) return;
    try { window.close(); } catch (_) {}
  }, [authModalOpen, token]);

  // Sign-in popup: opened by a standalone ?embed=auth button with no converter
  // frame to hand off to. Only the auth dialog belongs here — the window is
  // ~480px wide and closes itself once AppContext has handed the session back
  // to its opener.
  if (isAuthPopup) {
    return (
      <>
        <AuthModal />
        <Snackbar
          open={snackbar.open}
          autoHideDuration={SNACKBAR_AUTO_HIDE_MS}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          message={snackbar.message}
        />
      </>
    );
  }

  // Button-only modes: no SEO block, no page chrome, no consent banner (the
  // converter frame on the same page already renders one). The auth modal and
  // snackbar still mount so sign-in works inside this frame.
  if (isButtonEmbed) {
    return (
      <>
        {isAuthEmbed ? <AuthButtonEmbed /> : <PlanButtonEmbed {...planEmbedConfig} />}
        <AuthModal />
        <Snackbar
          open={snackbar.open}
          autoHideDuration={SNACKBAR_AUTO_HIDE_MS}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          message={snackbar.message}
        />
      </>
    );
  }

  return (
    <>
      <SEO />

      {currentPage === 'converter' && <ConversionPage />}
      {currentPage === 'billing' && <BillingPortal />}
      {currentPage === 'subscription-confirmed' && <SubscriptionConfirmed />}

      <AuthModal />

      {/* Account dialog for the standalone sign-in button (see the
          REQUEST_ACCOUNT handler above). */}
      <Dialog open={accountDialogOpen} onClose={() => setAccountDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Account</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {(() => {
              const who = email || window.localStorage.getItem('userEmail') || '';
              return who ? `Signed in as ${who}.` : 'Signed in.';
            })()}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAccountDialogOpen(false); setCurrentPage('billing'); }}>Billing portal</Button>
          <Button color="error" onClick={() => { handleLogout(); setAccountDialogOpen(false); }}>Logout</Button>
        </DialogActions>
      </Dialog>

      {/* Global confirmation dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{confirmDialog.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}>Cancel</Button>
          <Button
            variant="contained"
            color={confirmDialog.title?.includes('Cancel') ? 'error' : 'primary'}
            onClick={() => {
              setConfirmDialog({ ...confirmDialog, open: false });
              if (confirmDialog.onConfirm) confirmDialog.onConfirm();
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Global snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={SNACKBAR_AUTO_HIDE_MS}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />

      {/* Cookie/pixel consent banner — only renders when REACT_APP_REQUIRE_CONSENT=true */}
      <ConsentBanner />
    </>
  );
}

export default function App() {
  return (
    <AppProvider>
      <AppShell />
    </AppProvider>
  );
}
